/**
 * Group Security (v3) — feature-level UI flags.
 */

/**
 * Show the cross-teamspace **All view** (spec §1 / tasks T901–T905).
 *
 * The backend read path ALREADY returns each user's full cross-team reach and treats
 * `filters.teamId` as an optional filter (spec §1a), so All view is purely the FE surfacing of
 * data the list queries already get. This flag is the ONE switch for every All-view affordance:
 *  - the "All" entry in the TeamSwitcher (T901)
 *  - the Workspace column on all 5 list surfaces (T902)
 *  - the create-blocked-in-All gate (T904)
 *
 * ON for EVERYONE — no role gate (Ted, 2026-08-26: "We need that for SuperAdmin and Owner
 * anyway."). Whether we KEEP it is decided during testing, so restricting or removing it later
 * must be a ONE-LINE change here, never a hunt: flip to `false` to make every surface behave
 * exactly as it did before All view existed (scope collapses to the active teamspace, the column
 * disappears, create is never blocked).
 *
 * NOTE — reach-based hiding is NOT this flag's job and stays on regardless: when a user reaches a
 * single teamspace the All entry is pointless, so it is hidden for them (see
 * `selectIsAllViewAvailable` in `stores/team-store.ts`, which ANDs this flag with
 * `teams.length > 1`). Testing should revisit whether that reach test should also count
 * cross-team shared folders — today a single-teamspace user who has folders shared to them sees
 * them under "Shared with me" instead, and never gets the All entry.
 */
export const SHOW_ALL_VIEW = true;

/**
 * Move the teamspace chooser OUT of the profile menu and INTO the top bar, in the notification
 * bell's slot (spec `26-spec-topnav-0826.md`, tasks T980–T983).
 *
 * ONE switch for all four coupled changes, because they only make sense together:
 *  - the notification bell is HIDDEN (component / store / queries stay wired — N-Q1)
 *  - the standalone `TeamScopeButton` renders in the bell's slot (T981)
 *  - the chooser row is removed from `UserMenu` — it moves EXCLUSIVELY (N-Q3)
 *  - the profile chip becomes avatar-only: no team name, no chevron (N-Q2)
 *
 * `false` ⇒ exactly today's top bar: bell back, chooser back as the `UserMenu` submenu row, team
 * name back on the chip. One line, no other edit — Ted's explicit revert requirement (spec §6).
 *
 * NOT covered by this flag (deliberately): the one-team visibility guard fix
 * (`useCanSwitchTeamScope`, spec §4) and the Emergency bar's submenu chooser (N-Q5) — the guard is
 * a bug fix that should hold in both states, and the emergency bar has no room for a top-bar
 * control so it keeps the submenu either way.
 */
export const TOP_NAV_TEAM_SWITCHER = true;

/**
 * Sentinel scope value meaning "every teamspace I can reach" — the value the TeamSwitcher's All
 * entry writes into the team store's scope state (T901) and that every list surface reads to
 * decide whether to show the Workspace column and block create.
 *
 * Deliberately not a real teamId: '1' is the ROOT teamspace and a real teamId is a Mongo id.
 *
 * ⚠️ WIRE CONSTANT — this is the ONE definition in the frontend, and since T903 it is also sent on
 * the wire as `query.teamId` (see `selectListScopeTeamId` in `stores/team-store.ts`). The value
 * MUST stay identical to the backend's own sentinel (`optisigns-open-api`
 * `src/app/shared/base.service.ts`, which normalizes it away before any Mongo query is built — the
 * literal string must never reach Mongo, no team has `_id: 'all'`). Changing it here without
 * changing it there silently empties every list, so treat it as a contract, not a UI string.
 */
export const ALL_TEAMS_SCOPE = 'all';

/**
 * #2190 — the colour a NEW group starts with. It is a real value the operator sees, edits and saves,
 * never a painted-on display fallback: the list dot, the General tab swatch and the header dot all
 * render the STORED colour, and a group with none renders the grey "no colour" dot everywhere.
 */
export const DEFAULT_GROUP_COLOR = '#3b82f6';

/**
 * The Groups page's synthetic "Super Admin" row. Not a stored group: its members are the account's
 * Owners and Super Admins (access comes from the role), so the pop-up builds it read-only.
 */
export const SUPER_ADMIN_GROUP_ROW_ID = '__super-admin__';
